// src/components/Auth/ResetPasswordModal.tsx
import React from "react";
import { Modal, Input, Button, Alert, Form } from "antd";
import { resetPassword } from "../../Services/AuthenticationService/ForgotpasswordApi";
import AlertMessage from "../AlertMessage/AlertMessage";
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import "./Resetpassword.css";

interface ResetPasswordModalProps {
  visible: boolean;
  onClose: () => void;
  userId?: string;
}

// Yup validation schema
const validationSchema = Yup.object().shape({
  password: Yup.string()
    .required("Please enter your new password!")
    .min(8, "Password must be at least 8 characters")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[0-9]/, "Password must contain at least one number")
    .matches(
      /[@$!%*?&]/,
      "Password must contain at least one special character",
    ),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords do not match!")
    .required("Please confirm your password!"),
});

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
  visible,
  onClose,
  userId,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [alert, setAlert] = React.useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const handleSubmit = async (values: {
    password: string;
    confirmPassword: string;
  }) => {
    setLoading(true);
    setAlert(null);

    try {
      if (!userId) {
        throw new Error("User ID is required to reset password.");
      }

      await resetPassword({
        userId: userId,
        password: values.password,
      });

      AlertMessage({
        text: "reset password successfully",
        type: "success",
      });
      setLoading(false);

      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err: any) {
      setAlert({ type: "error", text: err.message || "Something went wrong" });
      setLoading(false);
      setTimeout(() => {
        setAlert(null);
      }, 3000);
    }
  };

  return (
    <Modal
      title="Reset Password"
      open={visible}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      {alert && (
        <Alert
          message={alert.text}
          type={alert.type}
          showIcon
          style={{ marginBottom: 15 }}
        />
      )}

      <Formik
        initialValues={{ password: "", confirmPassword: "" }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ handleSubmit }) => (
          <FormikForm onSubmit={handleSubmit}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                <tr style={{ maxHeight: "20px" }}>
                  <td
                    style={{
                      padding: "px",
                      width: "10%",
                      // backgroundColor: "#170505ff",
                      height: "30px",
                      display: "inline-block",
                    }}
                  >
                    <label
                      style={{
                        display: "inline-block", // label width lock
                        width: "120px",
                        paddingTop: "10px",
                      }}
                    >
                      New Password <span style={{ color: "red" }}>*</span>
                    </label>
                  </td>
                  <td style={{ padding: "8px" }}>
                    <Field
                      as={Input.Password}
                      name="password"
                      style={{ width: "100%" }}
                      placeholder="Enter new password"
                    />
                    <ErrorMessage
                      name="password"
                      component="div"
                      className="error-message"
                    />
                  </td>
                </tr>

                <tr>
                  <td
                    style={{
                      width: "10%",
                      // backgroundColor: "#170505ff",
                      height: "30px",
                      display: "inline-block",
                    }}
                  >
                    <label
                      style={{
                        display: "inline-block", // label width lock
                        width: "130px",
                        paddingTop: "10px",
                      }}
                    >
                      Confirm Password <span style={{ color: "red" }}>*</span>
                    </label>
                  </td>
                  <td style={{ padding: "8px" }}>
                    <Field
                      as={Input.Password}
                      name="confirmPassword"
                      style={{ width: "100%" }}
                      placeholder="Confirm your password"
                    />
                    <ErrorMessage
                      name="confirmPassword"
                      component="div"
                      className="error-message"
                    />
                  </td>
                </tr>

                <tr>
                  <td
                    colSpan={2}
                    style={{ padding: "8px", textAlign: "center" }}
                  >
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      style={{ backgroundColor: "var(--color-brand)" }}
                    >
                      Reset Password
                    </Button>
                  </td>
                </tr>
              </tbody>
            </table>
          </FormikForm>
        )}
      </Formik>
    </Modal>
  );
};

export default ResetPasswordModal;
